import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { TID } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadExtract = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/extract/first-chapter`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Metodo-di-Mare-Estratto-Capitolo-1.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Estratto scaricato. Buona lettura.");
    } catch (err) {
      toast.error("Errore nel download. Riprova.");
    } finally {
      setDownloading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (!consent) {
      toast.error("Devi accettare l'informativa privacy per iscriverti.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email, consent: true });
      toast.success("Iscritto. Ora scarico l'estratto per te...");
      await downloadExtract();
      setEmail("");
      setConsent(false);
    } catch (err) {
      toast.error("Email non valida o errore di rete.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid={TID.newsletterSection}
      className="relative py-24 md:py-32 border-t border-[#E5E1DA] overflow-hidden bg-gradient-to-br from-[#FFE8D6] via-[#F9F8F6] to-[#D6E5EC]"
    >
      <div className="max-w-2xl mx-auto px-6 text-center">
        <p className="overline">Estratto gratuito · Capitolo 1</p>
        <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">
          "La mia storia"
          <br />
          <em className="text-[#0E7C8F]">il primo capitolo, gratis.</em>
        </h2>
        <p className="mt-6 text-lg font-light text-[#3A4F6B]">
          Il sogno della libertà, l'Amerigo Vespucci, la prima crisi e la
          nascita del metodo. Iscriviti e ricevi il PDF in download immediato.
        </p>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-12 flex flex-col sm:flex-row items-end gap-4 max-w-xl mx-auto"
        >
          <div className="flex-1 text-left w-full">
            <label htmlFor="newsletter-email" className="overline block mb-2">
              La tua email
            </label>
            <input
              id="newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@esempio.com"
              data-testid={TID.newsletterEmail}
              className="w-full bg-transparent border-0 border-b border-[#0B192C] py-3 text-lg font-light text-[#0B192C] placeholder:text-[#3A4F6B]/50 focus:outline-none focus:border-b-2"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid={TID.newsletterSubmit}
            data-track="newsletter-submit"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-[#0B192C] text-[#F9F8F6] text-sm uppercase tracking-[0.2em] hover:bg-[#1E3557] transition-all disabled:opacity-60"
          >
            {loading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <>
                Ricevi & Scarica
                <ArrowRight
                  size={14}
                  className="transition-transform group-hover:translate-x-1"
                />
              </>
            )}
          </button>
        </motion.form>

        <label className="mt-5 flex items-start gap-3 text-left max-w-xl mx-auto cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="newsletter-consent-checkbox"
            className="mt-1 w-4 h-4 accent-[#E89B6C] flex-shrink-0"
          />
          <span className="text-xs font-light leading-relaxed text-[#3A4F6B]">
            Ho letto l'<a href="/privacy" className="underline text-[#0E7C8F] hover:text-[#E89B6C]">informativa privacy</a> e acconsento al trattamento della mia email per ricevere l'estratto gratuito del libro e comunicazioni relative al Metodo di Mare. Posso revocare il consenso in ogni momento.
          </span>
        </label>

        <div className="mt-8 flex items-center gap-4 justify-center">
          <span className="h-px w-12 bg-[#E5E1DA]" />
          <span className="overline text-[#3A4F6B]">oppure</span>
          <span className="h-px w-12 bg-[#E5E1DA]" />
        </div>

        <button
          onClick={downloadExtract}
          disabled={downloading}
          data-testid="newsletter-direct-download-btn"
          className="mt-6 inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] text-[#0B192C] link-underline disabled:opacity-60"
        >
          {downloading ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Preparazione...
            </>
          ) : (
            <>
              <Download size={14} />
              Scarica subito senza iscrizione
            </>
          )}
        </button>

        <p className="mt-6 text-xs uppercase tracking-[0.18em] text-[#3A4F6B]/60">
          PDF · 11 pagine · Nessuno spam, mai
        </p>
      </div>
    </section>
  );
}
